import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableHeader } from "@tiptap/extension-table-header";
import { TableCell } from "@tiptap/extension-table-cell";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough, ListBullets, ListNumbers,
  Quotes, Code, TextH, TextHOne, TextHTwo, TextHThree, Link as LinkIcon,
  ArrowCounterClockwise, ArrowClockwise, Table as TableIcon, MagicWand,
  BracketsCurly, HighlighterCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * RichEditor — TipTap-based rich-text editor wired for contract authoring.
 *
 * Props:
 *   value (string HTML) — current document HTML
 *   onChange(html)      — called on every keystroke
 *   onFormatAI()        — optional, called when user clicks the ✨ Format button
 *   onInsertVariable()  — optional, called when user clicks {{…}}
 *   placeholder         — placeholder for empty state
 *   readOnly            — disable editing
 */
export default function RichEditor({
  value = "",
  onChange,
  onFormatAI,
  onInsertVariable,
  placeholder = "Start typing your contract, or select a boilerplate to begin…",
  readOnly = false,
  testId = "rich-editor",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ link: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-[var(--accent)] underline" } }),
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    editable: !readOnly,
    editorProps: {
      attributes: {
        "data-testid": testId,
        class: "prose prose-invert max-w-none focus:outline-none min-h-[500px] px-8 py-6",
      },
    },
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
  });

  // Keep editor content in sync when parent swaps `value` (e.g. boilerplate selected)
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value && value != null) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) {
    return <div className="min-h-[500px] flex items-center justify-center text-sm text-[var(--text-3)]">Loading editor…</div>;
  }

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] overflow-hidden" data-testid={`${testId}-wrap`}>
      <Toolbar editor={editor} onFormatAI={onFormatAI} onInsertVariable={onInsertVariable} />
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor, onFormatAI, onInsertVariable }) {
  const btn = (label, active, onClick, Icon, testId) => (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      data-testid={testId}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition ${active ? "bg-[var(--surface-strong)] text-[var(--accent)]" : "text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"}`}
    >
      <Icon size={14} weight={active ? "fill" : "regular"} />
    </button>
  );
  const sep = <span className="mx-1 h-5 w-px bg-[var(--border)]" />;

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-2 border-b border-[var(--border)] bg-[var(--surface-2)]/60 sticky top-0 z-10 backdrop-blur">
      {btn("Heading 1",   editor.isActive("heading", { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), TextHOne, "rt-h1")}
      {btn("Heading 2",   editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), TextHTwo, "rt-h2")}
      {btn("Heading 3",   editor.isActive("heading", { level: 3 }), () => editor.chain().focus().toggleHeading({ level: 3 }).run(), TextHThree, "rt-h3")}
      {btn("Paragraph",   editor.isActive("paragraph"), () => editor.chain().focus().setParagraph().run(), TextH, "rt-p")}
      {sep}
      {btn("Bold",        editor.isActive("bold"),        () => editor.chain().focus().toggleBold().run(), TextB, "rt-bold")}
      {btn("Italic",      editor.isActive("italic"),      () => editor.chain().focus().toggleItalic().run(), TextItalic, "rt-italic")}
      {btn("Underline",   editor.isActive("underline"),   () => editor.chain().focus().toggleUnderline().run(), TextUnderline, "rt-underline")}
      {btn("Strikethrough", editor.isActive("strike"),    () => editor.chain().focus().toggleStrike().run(), TextStrikethrough, "rt-strike")}
      {sep}
      {btn("Bulleted list", editor.isActive("bulletList"),  () => editor.chain().focus().toggleBulletList().run(), ListBullets, "rt-ul")}
      {btn("Numbered list", editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), ListNumbers, "rt-ol")}
      {btn("Block quote",   editor.isActive("blockquote"),  () => editor.chain().focus().toggleBlockquote().run(), Quotes, "rt-quote")}
      {btn("Code block",    editor.isActive("codeBlock"),   () => editor.chain().focus().toggleCodeBlock().run(), Code, "rt-code")}
      {sep}
      {btn("Insert link", editor.isActive("link"), () => {
        const prev = editor.getAttributes("link").href || "";
        const url = window.prompt("Link URL (blank to remove)", prev);
        if (url === null) return;
        if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
      }, LinkIcon, "rt-link")}
      {btn("Insert table", false, () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(), TableIcon, "rt-table")}
      {sep}
      {btn("Undo", false, () => editor.chain().focus().undo().run(), ArrowCounterClockwise, "rt-undo")}
      {btn("Redo", false, () => editor.chain().focus().redo().run(), ArrowClockwise, "rt-redo")}

      <div className="flex-1" />

      {onInsertVariable && (
        <Button size="sm" variant="ghost" onClick={onInsertVariable} data-testid="rt-insert-variable" className="h-7 gap-1.5 text-xs">
          <BracketsCurly size={12} /> Variable
        </Button>
      )}
      {onFormatAI && (
        <Button size="sm" variant="outline" onClick={onFormatAI} data-testid="rt-ai-format" className="h-7 gap-1.5 text-xs">
          <MagicWand size={12} /> AI format
        </Button>
      )}
    </div>
  );
}

/** Utility — strip HTML tags to produce a plain-text mirror for the server.
 *  Uses DOMParser so that scripts, event handlers, and image loads NEVER fire
 *  (safe for untrusted HTML; see HTML5 DOMParser spec — parsed docs are inert).
 */
export function htmlToPlain(html) {
  if (!html) return "";
  if (typeof DOMParser === "undefined") return html.replace(/<[^>]+>/g, "");
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body?.textContent || "";
}
